<template>
  <div class="max-w-3xl mx-auto dark:bg-gray-900 dark:text-gray-100">
    <!-- Exam Header -->
    <div class="mb-6">
      <div class="flex justify-between items-center">
        <div>
          <nav class="text-sm text-gray-600 dark:text-gray-400 mb-2">
            <router-link to="/" class="hover:text-blue-600 dark:hover:text-blue-400">Home</router-link>
            <span class="mx-2">›</span>
            <router-link :to="`/exam-select/${year}/${subject}`" class="hover:text-blue-600 dark:hover:text-blue-400">
              {{ year }} {{ subject }}
            </router-link>
            <span class="mx-2">›</span>
            <span>{{ examSetLabel }}</span>
          </nav>
          <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">{{ subject }} Exam ({{ year }})</h2>
          <p class="text-gray-600 dark:text-gray-300">{{ examSetLabel }} - {{ examMode === 'sample' ? 'Sample Version' : 'Full Exam' }}</p>
        </div>
        <span v-if="!examCompleted" class="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 py-1 px-3 rounded-full text-sm font-medium">
          Question {{ currentQuestionIndex + 1 }} of {{ examQuestions.length }}
        </span>
      </div>
      
      <!-- Timer Settings -->
      <div v-if="!examStarted && !examCompleted" class="mt-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="flex items-center mb-3">
          <input
            type="checkbox"
            id="timedExam"
            v-model="timedMode"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-blue-600 dark:ring-offset-gray-800"
          />
          <label for="timedExam" class="ml-2 text-gray-700 dark:text-gray-300 font-medium">Timed Exam</label>
        </div>
        
        <div v-if="timedMode" class="ml-6 flex items-center">
          <label for="duration" class="mr-3 text-gray-600 dark:text-gray-400">Duration:</label>
          <select
            id="duration"
            v-model="selectedDuration"
            class="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 dark:focus:border-blue-500 dark:focus:ring-blue-500"
          >
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="45">45 minutes</option>
            <option value="60">60 minutes</option>
          </select>
        </div>
        
        <button
          @click="startExam"
          class="mt-4 bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded font-medium transition-colors dark:bg-green-500 dark:hover:bg-green-600"
        >
          Start Exam
        </button>
      </div>
      
      <!-- Timer Display -->
      <div v-if="examStarted && !examCompleted && timedMode" class="mt-4 bg-yellow-50 dark:bg-yellow-900 p-3 rounded-lg border border-yellow-200 dark:border-yellow-700">
        <div class="flex justify-between items-center">
          <span class="text-gray-700 dark:text-yellow-200">Time Remaining:</span>
          <span
            class="font-mono text-lg font-bold"
            :class="{'text-red-600 dark:text-red-400': timeRemaining < 60, 'text-yellow-600 dark:text-yellow-400': timeRemaining < 300 && timeRemaining >= 60, 'text-gray-800 dark:text-gray-200': timeRemaining >= 300}"
          >
            {{ formatTime(timeRemaining) }}
          </span>
        </div>
      </div>
      
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 dark:border-blue-400 border-t-transparent"></div>
        <p class="mt-2 text-gray-600 dark:text-gray-400">Loading exam questions...</p>
      </div>
      
      <div v-else-if="error" class="bg-red-100 dark:bg-red-900 border-l-4 border-red-500 dark:border-red-400 text-red-700 dark:text-red-200 p-4 mt-4" role="alert">
        <p class="font-bold">Error</p>
        <p>{{ error }}</p>
        <router-link :to="`/exam-select/${year}/${subject}`" class="block mt-4 text-blue-600 dark:text-blue-400 hover:underline">
          Return to Exam Selection
        </router-link>
      </div>
    </div>
    
    <!-- Exam Interface -->
    <div v-if="!loading && !error" class="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
      <!-- Exam in Progress -->
      <div v-if="examStarted && !examCompleted" class="space-y-6">
        <div class="question-container">
          <h3 class="text-xl font-medium text-gray-900 dark:text-gray-100 mb-4">{{ currentQuestion.question }}</h3>
          
          <div class="space-y-3">
            <label
              v-for="(option, index) in currentQuestion.options"
              :key="index"
              class="flex items-start p-3 rounded-md cursor-pointer"
              :class="{'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600': userAnswers[currentQuestionIndex] !== option,
                      'bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800': userAnswers[currentQuestionIndex] === option}"
            >
              <input
                type="radio"
                :name="`question-${currentQuestionIndex}`"
                :value="option"
                v-model="userAnswers[currentQuestionIndex]"
                class="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800"
              />
              <span class="ml-3 text-gray-800 dark:text-gray-200">{{ option }}</span>
            </label>
          </div>
        </div>
        
        <div class="flex justify-between mt-6">
          <button
            v-if="currentQuestionIndex > 0"
            @click="previousQuestion"
            class="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 py-2 px-4 rounded transition-colors"
          >
            Previous
          </button>
          <div v-else></div>
          
          <button
            @click="nextQuestion"
            class="bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600 py-2 px-6 rounded font-medium transition-colors"
            :disabled="!userAnswers[currentQuestionIndex]"
            :class="{'opacity-50 cursor-not-allowed': !userAnswers[currentQuestionIndex]}"
          >
            {{ isLastQuestion ? 'Submit' : 'Next' }}
          </button>
        </div>
      </div>
      
      <!-- Exam Results -->
      <div v-else-if="examCompleted" class="space-y-8">
        <div class="result-summary text-center py-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-2xl font-bold mb-2 dark:text-gray-100">Exam Complete!</h3>
          <p class="text-lg dark:text-gray-300">Your Score:
            <span class="text-xl font-bold" :class="scoreColor">
              {{ score }}/{{ examQuestions.length }}
            </span>
          </p>
          <p class="text-gray-600 dark:text-gray-400 mt-1">{{ scorePercentage }}%</p>
          <p v-if="examMode === 'sample'" class="mt-2 text-blue-600 dark:text-blue-400 text-sm">
            This was a sample exam. Try the full version for more questions!
          </p>
          <p v-if="timedMode && timedOut" class="mt-2 text-red-600 dark:text-red-400">
            Time expired! Your exam was automatically submitted.
          </p>
          <p v-else-if="timedMode" class="mt-2 text-gray-600 dark:text-gray-400">
            Completed in: {{ formatTime(examDuration) }}
          </p>
        </div>
        
        <div class="space-y-6">
          <h4 class="text-lg font-semibold dark:text-gray-100">Review Your Answers:</h4>
          
          <div v-for="(question, index) in examQuestions" :key="index" class="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4 last:border-0">
            <div class="flex gap-2 mb-2">
              <span class="bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">
                {{ index + 1 }}
              </span>
              <h5 class="text-gray-900 dark:text-gray-100 font-medium">{{ question.question }}</h5>
            </div>
            
            <div class="pl-8 space-y-2">
              <div v-for="option in question.options" :key="option" class="py-1 px-2 rounded-md"
                :class="{
                  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': option === question.answer,
                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': option === userAnswers[index] && option !== question.answer,
                  'text-gray-600 dark:text-gray-400': option !== question.answer && option !== userAnswers[index]
                }">
                {{ option }}
                <span v-if="option === question.answer" class="ml-2 text-green-600 dark:text-green-400 text-sm">(Correct Answer)</span>
                <span v-else-if="option === userAnswers[index]" class="ml-2 text-red-600 dark:text-red-400 text-sm">(Your Answer)</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4 mt-6">
          <button
            @click="retryExam"
            class="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-6 rounded transition-colors dark:bg-yellow-600 dark:hover:bg-yellow-700"
          >
            Retry This Exam
          </button>
          <router-link
            :to="`/exam-select/${year}/${subject}`"
            class="bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600 font-medium py-2 px-6 rounded transition-colors text-center"
          >
            Try Another Exam
          </router-link>
          <router-link
            to="/"
            class="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 font-medium py-2 px-6 rounded transition-colors text-center"
          >
            Return to Home
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router' // Added useRouter
import subjectsData from '../assets/subjects.json'
import { useAuth } from '../composables/useAuth'
import { db } from '../firebaseConfig'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

export default {
  name: 'ExamView',
  props: {
    year: {
      type: String,
      required: true
    },
    subject: {
      type: String, 
      required: true
    },
    setId: {
      type: String,
      required: true
    }
  },
  
  setup(props) {
    const route = useRoute()
    const router = useRouter() // Import and use router for navigation
    const { currentUser } = useAuth()
    const loading = ref(true)
    const error = ref(null)
    const availableQuestions = ref([])
    const examQuestions = ref([])
    const currentQuestionIndex = ref(0)
    const userAnswers = ref([])
    const examCompleted = ref(false)
    const examStarted = ref(false)
    const examSetLabel = ref('')
    const examMode = ref('sample') // 'sample' or 'full'
    
    // Timer functionality
    const timedMode = ref(false)
    const selectedDuration = ref(30) // default to 30 minutes
    const timeRemaining = ref(0)
    const timerInterval = ref(null)
    const timedOut = ref(false)
    const examStartTime = ref(0)
    const examDuration = ref(0)
    
    // Number of questions for sample mode
    const SAMPLE_QUESTIONS_COUNT = 5
    
    const currentQuestion = computed(() => {
      if (examQuestions.value.length === 0) return {}
      return examQuestions.value[currentQuestionIndex.value]
    })
    
    const isLastQuestion = computed(() => {
      return currentQuestionIndex.value === examQuestions.value.length - 1
    })
    
    const score = computed(() => {
      if (!examCompleted.value) return 0
      
      return examQuestions.value.reduce((total, question, index) => {
        return total + (userAnswers.value[index] === question.answer ? 1 : 0)
      }, 0)
    })
    
    const scorePercentage = computed(() => {
      return Math.round((score.value / examQuestions.value.length) * 100)
    })
    
    const scoreColor = computed(() => {
      const percentage = scorePercentage.value
      if (percentage >= 80) return 'text-green-600'
      if (percentage >= 60) return 'text-yellow-600'
      return 'text-red-600'
    })
    
    // Timer functions
    const startTimer = () => {
      if (timedMode.value) {
        timeRemaining.value = selectedDuration.value * 60 // convert minutes to seconds
        examStartTime.value = Date.now()
        
        timerInterval.value = setInterval(() => {
          if (timeRemaining.value > 0) {
            timeRemaining.value--
          } else {
            // Time expired, auto-submit exam
            timedOut.value = true
            submitExam()
          }
        }, 1000)
      }
    }
    
    const stopTimer = () => {
      if (timerInterval.value) {
        clearInterval(timerInterval.value)
        
        // Calculate exam duration if exam completed before timeout
        if (!timedOut.value) {
          examDuration.value = Math.floor((Date.now() - examStartTime.value) / 1000) // duration in seconds
        } else {
          // If timed out, duration equals the full selected time
          examDuration.value = selectedDuration.value * 60
        }
      }
    }
    
    const formatTime = (seconds) => {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }
    
    const startExam = () => {
      examStarted.value = true
      if (timedMode.value) {
        startTimer()
      }
    }
    
    const submitExam = async () => {
      stopTimer()
      examCompleted.value = true

      if (currentUser.value && examMode.value === 'full') { // Only save full exams for logged-in users
        try {
          const examAttempt = {
            userId: currentUser.value.uid,
            examSetId: props.setId,
            examName: examSetLabel.value,
            subject: props.subject,
            year: props.year,
            score: score.value,
            totalQuestions: examQuestions.value.length,
            scorePercentage: scorePercentage.value,
            date: serverTimestamp(),
            mode: examMode.value,
            completedDuration: timedMode.value ? examDuration.value : null // Store duration if timed
          };
          await addDoc(collection(db, "examAttempts"), examAttempt);
          console.log("Exam attempt saved for user:", currentUser.value.uid);
        } catch (e) {
          console.error("Error adding document: ", e);
          // Optionally, inform the user that their results couldn't be saved
        }
      }
    }
    
    // Function to load exam questions
    const loadExamQuestions = () => {
      try {
        // Get exam mode from query parameters
        examMode.value = route.query.mode || 'sample'
        
        const yearData = subjectsData[props.year]
        
        if (!yearData) {
          error.value = `Year "${props.year}" not found`
          loading.value = false
          return
        }
        
        const subjectData = yearData[props.subject]
        
        if (!subjectData) {
          error.value = `Subject "${props.subject}" not found for ${props.year}`
          loading.value = false
          return
        }
        
        // Find the specific exam set
        const examSet = subjectData.examSets?.find(set => set.id === props.setId)
        
        if (!examSet) {
          error.value = `Exam set "${props.setId}" not found for ${props.subject} in ${props.year}`
          loading.value = false
          return
        }
        
        examSetLabel.value = examSet.label
        availableQuestions.value = [...examSet.questions]
        
        let questionsToUse = []
        let questionCount = 0
        
        // Determine question count based on mode
        if (examMode.value === 'sample') {
          questionCount = Math.min(SAMPLE_QUESTIONS_COUNT, availableQuestions.value.length)
          
          // Select random questions for sample
          const shuffled = [...availableQuestions.value]
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
          }
          questionsToUse = shuffled.slice(0, questionCount)
        } else {
          // Full exam - use all questions
          questionCount = availableQuestions.value.length
          questionsToUse = [...availableQuestions.value]
        }
        
        // Initialize user answers array
        userAnswers.value = Array(questionCount).fill('')
        examQuestions.value = questionsToUse
        loading.value = false
      } catch (err) {
        console.error('Error loading exam questions:', err)
        error.value = 'Failed to load exam questions. Please try again.'
        loading.value = false
      }
    }

    const resetExamState = () => {
      // Reset all relevant refs to their initial states
      currentQuestionIndex.value = 0;
      userAnswers.value = Array(examQuestions.value.length).fill('');
      examCompleted.value = false;
      examStarted.value = false; // This will show the timer settings again
      timedMode.value = false; // Or retain user's previous timedMode preference
      selectedDuration.value = 30; // Or retain
      timeRemaining.value = 0;
      timedOut.value = false;
      examStartTime.value = 0;
      examDuration.value = 0;
      if (timerInterval.value) {
        clearInterval(timerInterval.value);
        timerInterval.value = null;
      }
      // No need to reload questions if it's a retry of the same set
      // but if questions were randomized per attempt, you'd call loadExamQuestions()
    };

    const retryExam = () => {
      resetExamState();
      // Optional: If you want to force a re-render or ensure reactivity,
      // you could navigate to the same route, but usually resetting state is enough.
      // For example, to ensure query params like 'mode' are correctly re-evaluated if they affect setup:
      // router.push({ name: 'exam', params: { year: props.year, subject: props.subject, setId: props.setId }, query: { mode: examMode.value, t: Date.now() } });
      // Adding a timestamp query param `t` can help force re-evaluation if Vue's reactivity doesn't pick up changes for a route to itself.
      // However, for this case, simply resetting state should make the v-if directives re-evaluate and show the start screen.
      // If loadExamQuestions needs to be called (e.g., for new randomization), call it here.
      // loadExamQuestions(); // Call if questions need to be re-randomized or re-fetched for a retry
    };
    
    const nextQuestion = () => {
      if (isLastQuestion.value) {
        submitExam()
        return
      }
      
      if (userAnswers.value[currentQuestionIndex.value]) {
        currentQuestionIndex.value++
      }
    }
    
    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        currentQuestionIndex.value--
      }
    }
    
    // Clean up timer when component is destroyed
    onBeforeUnmount(() => {
      if (timerInterval.value) {
        clearInterval(timerInterval.value)
      }
    })
    
    onMounted(() => {
      loadExamQuestions()
    })
    
    return {
      loading,
      error,
      examQuestions,
      currentQuestionIndex,
      currentQuestion,
      userAnswers,
      isLastQuestion,
      examCompleted,
      examStarted,
      examSetLabel,
      examMode,
      score,
      scorePercentage,
      scoreColor,
      nextQuestion,
      previousQuestion,
      timedMode,
      selectedDuration,
      timeRemaining,
      timedOut,
      examDuration,
      formatTime,
      startExam,
      retryExam
    }
  }
}
</script>